import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Search, MapPin, ChefHat, ShoppingBag } from "lucide-react";
import { supabase } from "../lib/supabase";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import RestaurantCard from "../components/common/RestaurantCard";
import { Spinner } from "../components/common/UI";

export default function HomePage() {
  const [search, setSearch] = useState("");
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState({ restaurants: 0, orders: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      const [{ data: restaurants }, { count: orderCount }] = await Promise.all([
        supabase
          .from("restaurants")
          .select("*")
          .eq("is_active", true)
          .eq("is_featured", true)
          .order("rating", { ascending: false })
          .limit(6),
        supabase.from("orders").select("*", { count: "exact", head: true }),
      ]);
      const { count: restCount } = await supabase
        .from("restaurants")
        .select("*", { count: "exact", head: true })
        .eq("is_active", true);
      setFeatured(restaurants || []);
      setStats({ restaurants: restCount || 0, orders: orderCount || 0 });
      setLoading(false);
    };
    load();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/restaurants?q=${encodeURIComponent(search)}`);
  };

  const cities = ["Burewala", "Vehari"];
  const categories = [
    { name: "Biryani", emoji: "🍚" },
    { name: "BBQ", emoji: "🔥" },
    { name: "Karahi", emoji: "🫕" },
    { name: "Desi", emoji: "🍛" },
    { name: "Fast Food", emoji: "🍔" },
    { name: "Sweets", emoji: "🍮" },
  ];

  return (
    <>
      <Helmet>
        <title>Khana Spot — Order Food Online in Burewala & Vehari</title>
        <meta name="description" content="Order delicious food online from the best restaurants in Burewala and Vehari. Fresh food, fast delivery." />
        <meta property="og:title" content="Khana Spot — Order Food Online" />
        <meta property="og:description" content="Best restaurants in Burewala and Vehari" />
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 text-white overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          {Array.from({ length: 20 }).map((_, i) => (
            <span
              key={i}
              className="absolute text-4xl select-none"
              style={{
                left: `${(i * 13) % 100}%`,
                top: `${(i * 17) % 100}%`,
                transform: `rotate(${i * 30}deg)`,
              }}
            >
              🍽️
            </span>
          ))}
        </div>

        <div className="relative max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">
            Hungry? We've got you<br />
            <span className="text-yellow-300">covered! 🍽️</span>
          </h1>
          <p className="mt-4 text-lg text-orange-100 max-w-xl mx-auto">
            Order from the best restaurants in{" "}
            {cities.map((c, i) => (
              <span key={c}>
                <strong className="text-yellow-300">{c}</strong>
                {i < cities.length - 1 ? " and " : ""}
              </span>
            ))}
          </p>

          {/* Search */}
          <form onSubmit={handleSearch} className="mt-8 max-w-xl mx-auto">
            <div className="flex bg-white rounded-2xl shadow-2xl overflow-hidden p-1.5">
              <div className="flex items-center pl-3 text-gray-400">
                <Search className="w-5 h-5" />
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search restaurants or dishes..."
                className="flex-1 px-3 py-3 text-gray-900 placeholder-gray-400 focus:outline-none text-sm"
              />
              <button
                type="submit"
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-2.5 rounded-xl transition-colors text-sm"
              >
                Search
              </button>
            </div>
          </form>

          {/* Stats */}
          <div className="mt-10 flex justify-center gap-8">
            {[
              { icon: ChefHat, label: "Restaurants", value: stats.restaurants },
              { icon: ShoppingBag, label: "Orders Placed", value: stats.orders },
              { icon: MapPin, label: "Cities", value: 2 },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="text-center">
                <div className="text-2xl font-black text-yellow-300">{value}+</div>
                <div className="text-xs text-orange-200 mt-0.5">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((cat) => (
              <Link
                key={cat.name}
                to={`/restaurants?cuisine=${encodeURIComponent(cat.name)}`}
                className="flex-shrink-0 flex flex-col items-center gap-1.5 bg-orange-50 hover:bg-orange-100 rounded-2xl px-5 py-3 transition-colors group"
              >
                <span className="text-2xl group-hover:scale-110 transition-transform">{cat.emoji}</span>
                <span className="text-xs font-semibold text-gray-700">{cat.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Restaurants */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-display text-2xl font-bold text-gray-900">
              ⭐ Featured Restaurants
            </h2>
            <p className="text-gray-500 text-sm mt-1">Top-rated places near you</p>
          </div>
          <Link to="/restaurants" className="text-sm font-semibold text-orange-500 hover:text-orange-600 transition-colors">
            View all →
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : featured.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <p className="text-4xl mb-3">🍽️</p>
            <p className="font-medium">Restaurants coming soon!</p>
            <p className="text-sm">Check back later for listings in your city.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.map((r) => (
              <RestaurantCard key={r.id} restaurant={r} />
            ))}
          </div>
        )}
      </section>

      {/* CTA for restaurants */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl p-8 flex flex-col sm:flex-row items-center gap-6">
          <div className="flex-1 text-center sm:text-left">
            <h2 className="font-display text-2xl font-bold text-white">
              Own a Restaurant? 🍴
            </h2>
            <p className="text-gray-400 mt-2 text-sm">
              Join Khana Spot and reach thousands of hungry customers in your city.
            </p>
          </div>
          <Link
            to="/register"
            className="flex-shrink-0 bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-3 rounded-xl transition-colors"
          >
            Partner With Us
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
}
